/**
 * Definições da conta. Três separadores: o perfil (o que os outros veem),
 * a palavra-passe, e as sessões activas — que é a forma de fechar a conta
 * num dispositivo que já não se tem à mão.
 *
 * O perfil escreve-se via `updateSelf` e o resultado é reinjectado no store
 * com um `UserUpdate` local, para a barra de baixo e as mensagens próprias
 * reflectirem a mudança sem esperar pelo eco do gateway.
 */

import { ACCENT_COLORS, AVATAR_FRAMES, BANNER_POS_PADRAO, type Gender } from "@greenvoice/shared";
import type { CSSProperties, PointerEvent as ReactPointerEvent } from "react";
import { useEffect, useRef, useState } from "react";
import type { SessionInfo } from "../lib/api";
import { api } from "../lib/api";
import { cssUrl } from "../lib/cssUrl";
import { formatMessageStamp } from "../lib/format";
import { disablePush, enablePush, getPushState, type PushState } from "../lib/push";
import {
  ACCENTS,
  DEFAULT_ACCENT,
  DEFAULT_THEME,
  loadTheme,
  normalizeHex,
  saveTheme,
  THEMES,
  type ThemeGroup,
  type ThemeState,
  themeAccent,
} from "../lib/theme";
import { UploadError, uploadImage } from "../lib/upload";
import { useStore } from "../store/store";
import { Avatar } from "./Avatar";
import { IconClose } from "./Icons";
import { ProfileCard } from "./ProfileCard";

type Tab = "profile" | "appearance" | "notifications" | "password" | "sessions";

/**
 * A navegação é agrupada de propósito: a APARÊNCIA da app (tema, cores) não é
 * uma definição de CONTA — é do dispositivo, vale antes de haver sessão. Separá-
 * la numa secção "Aplicação" evita a confusão de a meter debaixo de "conta".
 */
const NAV_GROUPS: { label: string; items: { id: Tab; label: string }[] }[] = [
  {
    label: "Conta",
    items: [
      { id: "profile", label: "Perfil" },
      { id: "password", label: "Senha" },
      { id: "sessions", label: "Sessões" },
    ],
  },
  {
    label: "Aplicativo",
    items: [
      { id: "appearance", label: "Aparência" },
      { id: "notifications", label: "Notificações" },
    ],
  },
];

export function UserSettings({ onClose }: { onClose: () => void }) {
  const self = useStore((s) => s.self);
  const applyEvent = useStore((s) => s.applyEvent);
  const [tab, setTab] = useState<Tab>("profile");

  if (!self) return null;

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label="Configurações">
      <div className="modal__panel settings__panel">
        <header className="modal__head">
          <h2>Configurações</h2>
          <button type="button" className="icon-button" onClick={onClose} title="Fechar">
            <IconClose size={18} />
          </button>
        </header>

        <div className="settings__layout">
          <nav className="settings__nav" role="tablist" aria-orientation="vertical">
            {NAV_GROUPS.map((group) => (
              <div key={group.label} className="settings__nav-group">
                <h3 className="settings__nav-label">{group.label}</h3>
                {group.items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    role="tab"
                    aria-selected={tab === item.id}
                    className={`settings__nav-item${tab === item.id ? " is-active" : ""}`}
                    onClick={() => setTab(item.id)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            ))}
          </nav>

          <div className="settings__body">
            {tab === "profile" ? <ProfileTab applyEvent={applyEvent} /> : null}
            {tab === "appearance" ? <AppearanceTab /> : null}
            {tab === "notifications" ? <NotificationsTab /> : null}
            {tab === "password" ? <PasswordTab /> : null}
            {tab === "sessions" ? <SessionsTab /> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Avatares acima disto são recusados pelo servidor — apanhamos aqui para dar erro cedo. */
const AVATAR_MAX_BYTES = 8 * 1024 * 1024;

function ProfileTab({ applyEvent }: { applyEvent: ReturnType<typeof useStore.getState>["applyEvent"] }) {
  const self = useStore((s) => s.self)!;
  const [displayName, setDisplayName] = useState(self.displayName ?? "");
  const [username, setUsername] = useState(self.username);
  const [bio, setBio] = useState(self.bio ?? "");
  const [statusText, setStatusText] = useState(self.statusText ?? "");
  // A cor e a moldura gravam-se com o resto do formulário, mas vêem-se ANTES:
  // é o par que o Discord tranca atrás do Nitro, e a graça de o ter de graça
  // é poder experimentar sem gravar nada.
  const [accent, setAccent] = useState(self.accentColor);
  const [frame, setFrame] = useState(self.avatarFrame);
  const [personagem, setPersonagem] = useState<Gender>(self.gender);
  const [bannerPos, setBannerPos] = useState(self.bannerPos ?? BANNER_POS_PADRAO);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // O avatar é a sua própria acção: um id de ficheiro não se escreve à mão, por
  // isso enviar E aplicar acontecem no momento do clique, sem esperar pelo
  // "Guardar" dos campos de texto.
  const fileRef = useRef<HTMLInputElement>(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarPct, setAvatarPct] = useState(0);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setError(null);
    setNotice(null);
    const trimmedUser = username.trim();
    try {
      const updated = await api.users.updateSelf({
        displayName: displayName.trim() || null,
        bio: bio.trim() || null,
        statusText: statusText.trim() || null,
        accentColor: accent,
        avatarFrame: frame,
        gender: personagem,
        bannerPos,
        ...(trimmedUser && trimmedUser !== self.username ? { username: trimmedUser } : {}),
      });
      applyEvent({ type: "UserUpdate", userId: updated.id, user: updated });
      setNotice("Perfil salvo.");
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function pickAvatar(file: File) {
    setAvatarError(null);
    if (!file.type.startsWith("image/")) {
      setAvatarError("Escolha uma imagem (PNG, JPG, GIF ou WebP).");
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      setAvatarError("A imagem é grande demais — o máximo são 8 MB.");
      return;
    }
    setAvatarBusy(true);
    setAvatarPct(0);
    try {
      const { fileId } = await uploadImage(file, "AVATAR", setAvatarPct);
      const updated = await api.users.updateSelf({ avatarId: fileId });
      applyEvent({ type: "UserUpdate", userId: updated.id, user: updated });
    } catch (caught) {
      // Cancelar não é um erro para mostrar em vermelho.
      if (!(caught instanceof UploadError && caught.aborted)) {
        setAvatarError((caught as Error).message);
      }
    } finally {
      setAvatarBusy(false);
    }
  }

  const bannerRef = useRef<HTMLInputElement>(null);
  const [bannerBusy, setBannerBusy] = useState(false);
  const [bannerError, setBannerError] = useState<string | null>(null);

  /**
   * Arrastar a capa para a enquadrar.
   *
   * A conta é a que se sente certa: puxar a imagem para CIMA mostra o que está
   * mais abaixo dela, portanto o deslocamento do rato entra com sinal
   * invertido. A escala é a altura da própria caixa — 96px de arrasto varrem a
   * imagem inteira, que é o que faz o gesto parecer que se está a mexer na
   * fotografia e não num controlo.
   *
   * `setPointerCapture` porque o rato SAI da caixa a meio do gesto quase
   * sempre: sem ele o arrasto morre no primeiro pixel fora e a imagem fica a
   * meio caminho.
   */
  function arrastar(e: ReactPointerEvent<HTMLDivElement>) {
    const caixa = e.currentTarget;
    const altura = caixa.getBoundingClientRect().height || 96;
    const partiuY = e.clientY;
    const partiuPos = bannerPos;
    caixa.setPointerCapture(e.pointerId);

    const mover = (ev: PointerEvent) => {
      const delta = ((ev.clientY - partiuY) / altura) * 100;
      setBannerPos(Math.round(Math.min(100, Math.max(0, partiuPos - delta))));
    };
    const largar = () => {
      caixa.removeEventListener("pointermove", mover);
      caixa.removeEventListener("pointerup", largar);
      caixa.removeEventListener("pointercancel", largar);
    };
    caixa.addEventListener("pointermove", mover);
    caixa.addEventListener("pointerup", largar);
    caixa.addEventListener("pointercancel", largar);
  }

  /** Enviar e aplicar de uma vez, como o avatar: um id de ficheiro não é coisa
   *  que se guarde num formulário à espera do «Salvar». */
  async function pickBanner(file: File) {
    setBannerError(null);
    if (!file.type.startsWith("image/")) {
      setBannerError("Escolha uma imagem (PNG, JPG, GIF ou WebP).");
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      setBannerError("A imagem é grande demais — o máximo são 8 MB.");
      return;
    }
    setBannerBusy(true);
    try {
      const { fileId } = await uploadImage(file, "BANNER");
      const updated = await api.users.updateSelf({ bannerId: fileId });
      applyEvent({ type: "UserUpdate", userId: updated.id, user: updated });
    } catch (caught) {
      if (!(caught instanceof UploadError && caught.aborted)) {
        setBannerError((caught as Error).message);
      }
    } finally {
      setBannerBusy(false);
    }
  }

  async function removeBanner() {
    setBannerBusy(true);
    try {
      const updated = await api.users.updateSelf({ bannerId: null });
      applyEvent({ type: "UserUpdate", userId: updated.id, user: updated });
    } catch (caught) {
      setBannerError((caught as Error).message);
    } finally {
      setBannerBusy(false);
    }
  }

  async function removeAvatar() {
    setAvatarError(null);
    setAvatarBusy(true);
    try {
      const updated = await api.users.updateSelf({ avatarId: null });
      applyEvent({ type: "UserUpdate", userId: updated.id, user: updated });
    } catch (caught) {
      setAvatarError((caught as Error).message);
    } finally {
      setAvatarBusy(false);
    }
  }

  // O que a pré-visualização mostra: o `self` verdadeiro com o que ainda está
  // por gravar por cima. Desenha-se com a MESMA peça que desenha o cartão de
  // perfil a sério (`ProfileCard`) — uma imitação era o caminho certo para
  // alguém escolher uma cor, gravar, e descobrir que afinal fica diferente.
  const espreitar = {
    ...self,
    displayName: displayName.trim() || null,
    bio: bio.trim() || null,
    statusText: statusText.trim() || null,
    accentColor: accent,
    avatarFrame: frame,
    bannerPos,
  };

  return (
    <form
      className="settings__form perfil"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <div className="perfil__grelha">
        <div className="perfil__campos">
          <div className="field">
            <span>Imagem de perfil</span>
            <div className="avatar-edit">
              <Avatar user={self} size={72} />
              <div className="avatar-edit__controls">
                <div className="avatar-edit__buttons">
                  <button
                    type="button"
                    className="button button--small"
                    disabled={avatarBusy}
                    onClick={() => fileRef.current?.click()}
                  >
                    {avatarBusy ? `Enviando… ${Math.round(avatarPct * 100)}%` : "Enviar imagem"}
                  </button>
                  {self.avatarUrl ? (
                    <button
                      type="button"
                      className="button button--small"
                      disabled={avatarBusy}
                      onClick={() => void removeAvatar()}
                    >
                      Remover
                    </button>
                  ) : null}
                </div>
                <p className="modal__hint">PNG, JPG, GIF ou WebP, até 8 MB.</p>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  // Limpar o valor deixa escolher o MESMO ficheiro outra vez (o
                  // `change` não dispara com o mesmo path).
                  e.target.value = "";
                  if (file) void pickAvatar(file);
                }}
              />
            </div>
            {avatarError ? (
              <p className="auth__error" role="alert">
                {avatarError}
              </p>
            ) : null}
          </div>

          <label className="field">
            <span>Nome de exibição</span>
            <input
              value={displayName}
              maxLength={64}
              placeholder={self.username}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </label>

          <label className="field">
            <span>Nome de usuário</span>
            <input value={username} maxLength={32} onChange={(e) => setUsername(e.target.value)} />
          </label>

          <label className="field">
            {/* «Estado» e não «status»: é a palavra que o resto da app usa (o menu do
            rodapé, o cartão de perfil, o selo) — e duas palavras para a mesma
            coisa fazem o leitor parar a perguntar se são a mesma. */}
            <span>Estado personalizado</span>
            <input
              value={statusText}
              maxLength={128}
              placeholder="Sem estado"
              onChange={(e) => setStatusText(e.target.value)}
            />
          </label>

          <label className="field">
            <span>Sobre mim</span>
            <textarea value={bio} maxLength={512} rows={3} onChange={(e) => setBio(e.target.value)} />
          </label>

          <div className="field">
            <span>Capa do perfil</span>
            <div className="banner-edit">
              {/* Arrasta-se a imagem para escolher o enquadramento — é o gesto
                  que toda a gente já conhece de qualquer capa. O `range` por
                  baixo faz exactamente o mesmo com o teclado: arrastar é
                  invisível para quem navega a Tab, e o enquadramento não pode
                  ser uma escolha só de quem tem rato. */}
              <div
                className={`banner-edit__preview${self.bannerUrl ? " is-arrastavel" : ""}`}
                style={
                  self.bannerUrl
                    ? { backgroundImage: cssUrl(self.bannerUrl), backgroundPositionY: `${bannerPos}%` }
                    : undefined
                }
                onPointerDown={self.bannerUrl ? arrastar : undefined}
              >
                {self.bannerUrl ? null : <span className="banner-edit__vazio">Sem capa</span>}
              </div>
              {self.bannerUrl ? (
                <label className="banner-edit__pos">
                  <span>Enquadramento</span>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={bannerPos}
                    onChange={(e) => setBannerPos(Number(e.target.value))}
                    aria-label="Enquadramento da capa, do topo ao fundo da imagem"
                  />
                  <small>{bannerPos}%</small>
                </label>
              ) : null}
              <div className="banner-edit__buttons">
                <button
                  type="button"
                  className="button button--small"
                  disabled={bannerBusy}
                  onClick={() => bannerRef.current?.click()}
                >
                  {bannerBusy ? "Enviando…" : "Enviar capa"}
                </button>
                {self.bannerUrl ? (
                  <button
                    type="button"
                    className="button button--small"
                    disabled={bannerBusy}
                    onClick={() => void removeBanner()}
                  >
                    Remover
                  </button>
                ) : null}
              </div>
              <input
                ref={bannerRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (file) void pickBanner(file);
                }}
              />
            </div>
            {bannerError ? <p className="auth__error">{bannerError}</p> : null}
          </div>

          <div className="field">
            <span>Cor do perfil</span>
            <div className="cores">
              <button
                type="button"
                className={`cores__opcao cores__opcao--nenhuma${accent === null ? " is-active" : ""}`}
                onClick={() => setAccent(null)}
                aria-label="Sem cor"
                aria-pressed={accent === null}
              />
              {ACCENT_COLORS.map((cor) => (
                <button
                  key={cor}
                  type="button"
                  className={`cores__opcao${accent === cor ? " is-active" : ""}`}
                  style={{ background: cor }}
                  onClick={() => setAccent(cor)}
                  aria-label={`Cor ${cor}`}
                  aria-pressed={accent === cor}
                />
              ))}
              <label className="cores__livre">
                <input
                  type="color"
                  value={accent ?? "#22c55e"}
                  onChange={(e) => setAccent(e.target.value)}
                  aria-label="Outra cor"
                />
                <span>Outra</span>
              </label>
            </div>
          </div>

          {/* A personagem do escritório 2D. Sem isto, a escolha da primeira
              entrada era para sempre — e um clique enganado num ecrã que só
              aparece uma vez não tem volta nenhuma. Mesma regra do outro ecrã:
              nem uma palavra sobre género, aqui ou no que se anuncia. */}
          <div className="field">
            <span>Personagem</span>
            <div className="personagens">
              <button
                type="button"
                className={`personagens__opcao${personagem === "MALE" ? " is-active" : ""}`}
                onClick={() => setPersonagem("MALE")}
                aria-pressed={personagem === "MALE"}
                aria-label="Personagem 1"
              >
                <span className="personagens__boneco personagens__boneco--um" />
              </button>
              <button
                type="button"
                className={`personagens__opcao${personagem === "FEMALE" ? " is-active" : ""}`}
                onClick={() => setPersonagem("FEMALE")}
                aria-pressed={personagem === "FEMALE"}
                aria-label="Personagem 2"
              >
                <span className="personagens__boneco personagens__boneco--dois" />
              </button>
            </div>
          </div>

          <div className="field">
            <span>Moldura do avatar</span>
            <div className="molduras">
              <button
                type="button"
                className={`molduras__opcao${frame === null ? " is-active" : ""}`}
                onClick={() => setFrame(null)}
                aria-pressed={frame === null}
              >
                <span className="molduras__anel molduras__anel--nenhuma" />
                <small>Nenhuma</small>
              </button>
              {AVATAR_FRAMES.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className={`molduras__opcao${frame === m.id ? " is-active" : ""}`}
                  onClick={() => setFrame(m.id)}
                  aria-pressed={frame === m.id}
                >
                  <span
                    className="molduras__anel"
                    style={{ "--moldura-a": m.colors[0], "--moldura-b": m.colors[1] } as CSSProperties}
                  />
                  <small>{m.name}</small>
                </button>
              ))}
            </div>
          </div>
        </div>

        <aside className="perfil__espreita">
          <span className="perfil__espreita-etiqueta">Como os outros te veem</span>
          <ProfileCard user={espreitar} />
          <p className="modal__hint">
            Muda a cor ou a moldura e vê aqui antes de gravar. No Discord isto é o Nitro; aqui é a conta que já tens.
          </p>
        </aside>
      </div>

      <div className="settings__foot">
        <button type="submit" className="button button--primary" disabled={busy}>
          Salvar
        </button>
        {notice ? (
          <span className="settings__notice" role="status">
            {notice}
          </span>
        ) : null}
      </div>

      {error ? (
        <p className="auth__error" role="alert">
          {error}
        </p>
      ) : null}
    </form>
  );
}

function AppearanceTab() {
  // O tema vive fora do store (é do dispositivo, vale antes de haver sessão), por
  // isso arranca do localStorage. Cada mudança guarda E aplica ao vivo — o utilizador
  // vê o resultado na app por trás do modal, não numa pré-visualização a fingir.
  const [state, setState] = useState<ThemeState>(() => loadTheme());
  const [customHex, setCustomHex] = useState(state.accent);

  function update(next: ThemeState) {
    setState(next);
    saveTheme(next);
  }

  function pickTheme(theme: string) {
    // Uma "vibe" só fica coesa se o acento acompanhar o fundo — por isso escolher
    // um tema com acento de assinatura também troca o acento (e o color-picker que
    // o reflecte). Os temas neutros não impõem cor: mantêm a escolha actual.
    const accent = themeAccent(theme) ?? state.accent;
    setCustomHex(accent);
    update({ theme, accent });
  }

  function pickAccent(accent: string) {
    setCustomHex(accent);
    update({ ...state, accent });
  }

  function tryCustomHex(raw: string) {
    setCustomHex(raw);
    const normalized = normalizeHex(raw);
    if (normalized) update({ ...state, accent: normalized });
  }

  function reset() {
    setCustomHex(DEFAULT_ACCENT);
    update({ theme: DEFAULT_THEME, accent: DEFAULT_ACCENT });
  }

  const isDefault = state.theme === DEFAULT_THEME && state.accent === DEFAULT_ACCENT;

  return (
    <div className="settings__form">
      <div className="field">
        <span>Tema</span>
        <p className="modal__hint">Muda a paleta de fundo e texto de todo o aplicativo.</p>
        <div role="radiogroup" aria-label="Tema">
          {(["Escuro", "Claro", "Vibe", "Cena"] as ThemeGroup[]).map((group) => {
            const inGroup = THEMES.filter((t) => t.group === group);
            if (inGroup.length === 0) return null;
            return (
              <section key={group} className="themes__group">
                <h4 className="themes__group-label">{group}</h4>
                <div className="themes">
                  {inGroup.map((theme) => (
                    <button
                      key={theme.id}
                      type="button"
                      role="radio"
                      aria-checked={state.theme === theme.id}
                      className={`theme-card${state.theme === theme.id ? " is-active" : ""}`}
                      onClick={() => pickTheme(theme.id)}
                    >
                      {theme.image ? (
                        <span
                          className="theme-card__preview theme-card__preview--photo"
                          style={{
                            backgroundColor: theme.swatch[0],
                            backgroundImage: `url("${theme.image}")`,
                          }}
                        >
                          <span
                            className="theme-card__dot"
                            style={{ background: theme.accent ?? state.accent }}
                            aria-hidden="true"
                          />
                        </span>
                      ) : (
                        <span className="theme-card__preview" style={{ background: theme.swatch[0] }}>
                          <span className="theme-card__bar" style={{ background: theme.swatch[1] }} />
                          <span
                            className="theme-card__dot"
                            style={{ background: theme.accent ?? state.accent }}
                            aria-hidden="true"
                          />
                          <span
                            className="theme-card__text"
                            style={{ background: theme.swatch[2] }}
                            aria-hidden="true"
                          />
                        </span>
                      )}
                      <span className="theme-card__name">{theme.name}</span>
                    </button>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </div>

      <div className="field">
        <span>Cor de acento</span>
        <p className="modal__hint">A cor dos botões, links e destaques.</p>
        <div className="accents" role="radiogroup" aria-label="Cor de acento">
          {ACCENTS.map((accent) => (
            <button
              key={accent.id}
              type="button"
              role="radio"
              aria-checked={state.accent.toLowerCase() === accent.hex.toLowerCase()}
              title={accent.name}
              className={`accent-swatch${state.accent.toLowerCase() === accent.hex.toLowerCase() ? " is-active" : ""}`}
              style={{ background: accent.hex }}
              onClick={() => pickAccent(accent.hex)}
            />
          ))}
          <label className="accent-custom" title="Cor personalizada">
            <input
              type="color"
              value={normalizeHex(customHex) ?? DEFAULT_ACCENT}
              onChange={(e) => pickAccent(e.target.value)}
            />
          </label>
        </div>
      </div>

      <label className="field">
        <span>Código da cor</span>
        <input
          value={customHex}
          spellCheck={false}
          placeholder="#22c55e"
          aria-invalid={normalizeHex(customHex) === null}
          onChange={(e) => tryCustomHex(e.target.value)}
        />
      </label>

      <div className="settings__foot">
        <button type="button" className="button" onClick={reset} disabled={isDefault}>
          Restaurar padrões
        </button>
        <span className="settings__notice" role="status">
          Salvo neste dispositivo.
        </span>
      </div>
    </div>
  );
}

function NotificationsTab() {
  // O push é do DISPOSITIVO (vive no PushManager deste browser), por isso o
  // estado lê-se do browser, não da conta. `null` = ainda a apurar.
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getPushState()
      .then((s) => {
        if (alive) setState(s);
      })
      .catch(() => {
        if (alive) setState("unsupported");
      });
    return () => {
      alive = false;
    };
  }, []);

  async function toggle(enable: boolean) {
    setBusy(true);
    setError(null);
    try {
      setState(enable ? await enablePush() : await disablePush());
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="settings__form">
      <div className="field">
        <span>Notificações do sistema</span>
        <p className="modal__hint">
          Avisa você de menções e mensagens diretas quando o GreenVoice está fechado ou em segundo plano. Ativa-se por
          dispositivo — cada navegador onde você entra é independente.
        </p>
      </div>

      {state === null ? <p className="modal__hint">Verificando…</p> : null}

      {state === "unsupported" ? (
        <p className="modal__hint">
          Este navegador não suporta notificações push. Num celular, instale primeiro o app (menu → Adicionar à tela
          inicial).
        </p>
      ) : null}

      {state === "denied" ? (
        <p className="modal__hint">
          As notificações estão bloqueadas nas configurações do navegador para este site. Desbloqueie-as no cadeado da
          barra de endereço e volte aqui.
        </p>
      ) : null}

      {state === "disabled" ? (
        <div className="settings__foot">
          <button type="button" className="button button--primary" disabled={busy} onClick={() => void toggle(true)}>
            {busy ? "Ativando…" : "Ativar notificações"}
          </button>
        </div>
      ) : null}

      {state === "enabled" ? (
        <div className="settings__foot">
          <button type="button" className="button" disabled={busy} onClick={() => void toggle(false)}>
            {busy ? "Desativando…" : "Desativar neste dispositivo"}
          </button>
          <span className="settings__notice" role="status">
            Ativadas neste dispositivo.
          </span>
        </div>
      ) : null}

      {error ? (
        <p className="auth__error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function PasswordTab() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mismatch = confirm.length > 0 && next !== confirm;

  async function save() {
    if (next !== confirm) {
      setError("A confirmação não coincide.");
      return;
    }
    if (next.length < 8) {
      setError("A nova senha precisa ter pelo menos 8 caracteres.");
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.auth.changePassword({ current, next });
      setNotice("Senha alterada.");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="settings__form"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <label className="field">
        <span>Senha atual</span>
        <input
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
        />
      </label>

      <label className="field">
        <span>Nova senha</span>
        <input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />
      </label>

      <label className="field">
        <span>Confirmar nova senha</span>
        <input
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
      </label>

      {mismatch ? <p className="modal__hint">As senhas não coincidem.</p> : null}

      <div className="settings__foot">
        <button type="submit" className="button button--primary" disabled={busy || !current || !next || mismatch}>
          Alterar senha
        </button>
        {notice ? (
          <span className="settings__notice" role="status">
            {notice}
          </span>
        ) : null}
      </div>

      {error ? (
        <p className="auth__error" role="alert">
          {error}
        </p>
      ) : null}
    </form>
  );
}

function SessionsTab() {
  const [sessions, setSessions] = useState<SessionInfo[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setBusy(true);
    setError(null);
    api.auth
      .sessions()
      .then((list) => {
        if (alive) setSessions(list);
      })
      .catch((caught) => {
        if (alive) setError((caught as Error).message);
      })
      .finally(() => {
        if (alive) setBusy(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  async function revoke(id: string) {
    try {
      await api.auth.revokeSession(id);
      setSessions((prev) => (prev ? prev.filter((s) => s.id !== id) : prev));
    } catch (caught) {
      setError((caught as Error).message);
    }
  }

  async function revokeOthers() {
    try {
      await api.auth.logoutAll();
      setSessions((prev) => (prev ? prev.filter((s) => s.current) : prev));
    } catch (caught) {
      setError((caught as Error).message);
    }
  }

  if (busy && sessions === null) return <p className="modal__hint">Carregando…</p>;

  const others = sessions?.filter((s) => !s.current).length ?? 0;

  return (
    <div className="settings__form">
      <p className="modal__hint">Cada dispositivo com sessão iniciada aparece aqui.</p>

      <ul className="sessions">
        {(sessions ?? []).map((session) => (
          <li key={session.id} className={`sessions__item${session.current ? " is-current" : ""}`}>
            <div className="sessions__info">
              <strong>{session.userAgent ?? "Cliente desconhecido"}</strong>
              <small>
                {session.ip ?? "IP oculto"} · ativa {formatMessageStamp(session.lastUsedAt)}
              </small>
            </div>
            {session.current ? (
              <span className="tag">Esta sessão</span>
            ) : (
              <button
                type="button"
                className="button button--small icon-button--danger"
                onClick={() => void revoke(session.id)}
              >
                Encerrar
              </button>
            )}
          </li>
        ))}
      </ul>

      {others > 0 ? (
        <button type="button" className="button" onClick={() => void revokeOthers()}>
          Encerrar as outras {others} sessões
        </button>
      ) : null}

      {error ? (
        <p className="auth__error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
